import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { Users, Car, Package, Truck, FileText, Layers } from "lucide-react";

export default function GlobalSearch({ open, setOpen }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [setOpen]);

  const search = useCallback(async (value) => {
    if (!value) { setRes(null); return; }
    try {
      const { data } = await api.get("/search", { params: { q: value } });
      setRes(data);
    } catch { setRes(null); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(q), 220);
    return () => clearTimeout(t);
  }, [q, search]);

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };

  return (
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
      <CommandInput
        placeholder="Pesquisar matrícula, cliente, referência, nº pedido…"
        value={q}
        onValueChange={setQ}
        data-testid="global-search-input"
      />
      <CommandList>
        {!res && <CommandEmpty>Escreva para pesquisar.</CommandEmpty>}
        {res && res.atendimentos?.length > 0 && (
          <CommandGroup heading="Pedidos">
            {res.atendimentos.map((a) => (
              <CommandItem key={a.id} onSelect={() => go(`/pedido/${a.id}`)} data-testid={`search-pedido-${a.numero}`}>
                <FileText size={15} className="mr-2 text-sky-600" />
                Pedido {a.codigo || `#${a.numero}`} · {a.cliente?.nome || "—"} · {a.veiculo?.matricula || ""}
                {(a.veiculo?.marca || a.veiculo?.modelo) && <span className="ml-1 text-slate-400">· {[a.veiculo?.marca, a.veiculo?.modelo].filter(Boolean).join(" ")}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {res && res.veiculos?.length > 0 && (
          <CommandGroup heading="Veículos">
            {res.veiculos.map((v) => (
              <CommandItem key={v.id} onSelect={() => go(`/registo/veiculo/${v.id}`)}>
                <Car size={15} className="mr-2 text-slate-600" />
                <span className="plate mr-2">{v.matricula}</span> {v.marca} {v.modelo}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {res && res.clientes?.length > 0 && (
          <CommandGroup heading="Clientes">
            {res.clientes.map((c) => (
              <CommandItem key={c.id} onSelect={() => go(`/registo/cliente/${c.id}`)}>
                <Users size={15} className="mr-2 text-slate-600" /> {c.nome} · {c.tipo}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {res && res.artigos?.length > 0 && (
          <CommandGroup heading="Artigos">
            {res.artigos.map((a) => (
              <CommandItem key={a.id} onSelect={() => go(`/registo/artigo/${a.id}`)}>
                <Package size={15} className="mr-2 text-slate-600" /> {a.descricao} · {a.ref_marca}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {res && res.fornecedores?.length > 0 && (
          <CommandGroup heading="Fornecedores">
            {res.fornecedores.map((f) => (
              <CommandItem key={f.id} onSelect={() => go(`/fornecedores`)}>
                <Truck size={15} className="mr-2 text-slate-600" /> {f.nome}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {res && res.encomendas?.length > 0 && (
          <CommandGroup heading="Encomendas">
            {res.encomendas.map((e) => (
              <CommandItem key={e.id} onSelect={() => go(`/encomendas`)} data-testid={`search-encomenda-${e.numero}`}>
                <Layers size={15} className="mr-2 text-indigo-600" />
                Encomenda {e.codigo || `#${e.numero}`} · {e.fornecedor?.nome || "—"} · {e.estado}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
